import React, {FC} from 'react';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {hideBannerAction} from '../../../../store/panels/app/banners/bannersActions';
import {
  WorkspacesErrorBannerState,
  WorkspacesErrorBannerTypesEnum,
} from '../../../../store/panels/app/banners/bannersState';

/*
 * Props.
 */

interface WorkspacesErrorBannerProps {
  state: WorkspacesErrorBannerState;
}

/*
 * Component.
 */

export const WorkspacesErrorBanner: FC<WorkspacesErrorBannerProps> = (props) => {
  const dispatch = useAppDispatch();
  const theme = useUserTheme();
  const {state} = props;

  const onDismissClick = () => {
    dispatch(hideBannerAction(state.uid));
  };

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.red.shade40}
      priority={BannerPrioritiesEnum.ERROR}
    >
      <BannerCloseAction onClick={onDismissClick} />
      <BannerIcon iconName="warningFilledSmall" />
      <BannerContent>{getBannerContent(state)}</BannerContent>
    </Banner>
  );
};

const getBannerContent = (state: WorkspacesErrorBannerState) => {
  switch (state.workspacesErrorType) {
    case WorkspacesErrorBannerTypesEnum.TOO_MANY_PINNED_LENSES:
      return (
        <>
          <BannerContentTitle>
            <Trans id="workspaces-error-banner-title-maximum-views-generic">Views limit reached</Trans>
          </BannerContentTitle>
          <Trans id="workspaces-error-banner-body-maximum-views-remove">
            Remove a shared view from your sidebar or delete a private view to add a new view to your sidebar.
          </Trans>{' '}
          {isDefined(state.pinnedLensesLimit) && (
            <Trans id="workspaces-error-banner-body-maximum-views-allowance">
              Only{' '}
              <Plural
                id="workspaces-error-banner-body-maximum-views-allowance-value"
                value={state.pinnedLensesLimit}
                one="# view"
                other="# views"
              />{' '}
              allowed.
            </Trans>
          )}
        </>
      );
    case WorkspacesErrorBannerTypesEnum.FALLBACK_UNKNOWN:
    default:
      return (
        <>
          <BannerContentTitle>
            <Trans id="workspaces-error-banner-title-unexpected">An unexpected error occurred.</Trans>
          </BannerContentTitle>
          <Trans id="workspaces-error-banner-body-unexpected">Please try again.</Trans>
        </>
      );
  }
};
